import { hash } from 'ohash'

import { definitionFingerprint, scopeFingerprint } from './versions'
import type { PolicyRole, PolicyStage } from './types'

/* A decision is the policy's only source of truth for where it stands: no
   freezing, no stored "current stage" that can drift from what actually
   happened. Every approve/challenge/respond/publish/reopen is pinned to the
   definition it saw, so an edit after the fact never silently keeps an
   approval that no longer describes the policy. */

export const DECISION_KINDS = [
  'approved',
  'challenged',
  'responded',
  'record-approved',
  'published',
  'reopened',
  'moved-back',
  'claims-attested',
  'withdrawn',
] as const
export type PolicyDecisionKind = (typeof DECISION_KINDS)[number]

export const DECISION_LABEL: Record<PolicyDecisionKind, string> = {
  approved: 'Approved',
  challenged: 'Sent back',
  responded: 'Responded',
  'record-approved': 'Record approved',
  published: 'Published',
  reopened: 'Reopened',
  'moved-back': 'Moved back',
  'claims-attested': 'Linked external test results',
  withdrawn: 'Update withdrawn',
}

export const CHANGE_TYPES = [
  'New policy',
  'Logic change',
  'Code set change',
  'Metadata change',
  'Defect fix',
] as const
export type ChangeType = (typeof CHANGE_TYPES)[number]

export interface VersionRecord {
  changeSummary: string
  changeType: ChangeType
  linesOfBusiness: string[]
}

export interface PolicyDecision {
  id: string
  seq: number
  at: string
  actor: string
  origin: PolicyStage
  destination: PolicyStage
  decision: PolicyDecisionKind
  version: number
  fingerprint: string
  scopeFingerprint: string
  note: string | null
  evidenceUrl: string | null
  /** The challenged decision id a response answers. */
  answers: string | null
  record: VersionRecord | null
  /** ohash of every other field, for tamper detection. */
  hash: string
}

export const ROLE_STAGE: Record<PolicyRole, PolicyStage> = {
  scoping: 'scoping',
  design: 'design',
  validation: 'validation',
}

export const ROLE_LABEL: Record<PolicyRole, string> = {
  scoping: 'Scoping',
  design: 'Design',
  validation: 'Validation',
}

export const isDriveUrl = (url: string): boolean =>
  /^https:\/\/(drive|docs)\.google\.com\//i.test(url.trim())

/** Hash of every field but `hash` itself, for tamper detection on a stored row. */
export const decisionHash = (entry: Omit<PolicyDecision, 'hash'>): string =>
  hash(entry)

export interface ApprovalState {
  decision: PolicyDecision | null
  current: boolean
}

/**
 * The latest `approved` decision per role, and whether it still counts.
 *
 * A scoping approval is current iff its `scopeFingerprint` matches the
 * view's current scope slice. A design or validation approval is current iff
 * its `fingerprint` matches the view's whole current definition. A challenge
 * does not invalidate an approval by itself — only a changed definition does.
 */
export const approvals = (
  decisions: PolicyDecision[],
  view: Parameters<typeof definitionFingerprint>[0] &
    Parameters<typeof scopeFingerprint>[0],
): Record<PolicyRole, ApprovalState> => {
  const fingerprint = definitionFingerprint(view)
  const scope = scopeFingerprint(view)
  const latestApprovalFor = (role: PolicyRole): PolicyDecision | null => {
    const origin = ROLE_STAGE[role]
    const matches = decisions.filter(
      (d) => d.decision === 'approved' && d.origin === origin,
    )
    return matches.length
      ? matches.reduce((a, b) => (b.seq > a.seq ? b : a))
      : null
  }
  const stateFor = (role: PolicyRole): ApprovalState => {
    const decision = latestApprovalFor(role)
    if (!decision) return { decision: null, current: false }
    const current =
      role === 'scoping'
        ? decision.scopeFingerprint === scope
        : decision.fingerprint === fingerprint
    return { decision, current }
  }
  return {
    scoping: stateFor('scoping'),
    design: stateFor('design'),
    validation: stateFor('validation'),
  }
}

/**
 * The latest `challenged` decision not yet followed by an `approved` or
 * `responded` decision into the stage it sent the policy to. `null` once
 * answered.
 */
export const latestChallenge = (
  decisions: PolicyDecision[],
): PolicyDecision | null => {
  const challenges = decisions.filter((d) => d.decision === 'challenged')
  if (!challenges.length) return null
  const challenge = challenges.reduce((a, b) => (b.seq > a.seq ? b : a))
  const answered = decisions.some(
    (d) =>
      d.seq > challenge.seq &&
      (d.decision === 'responded' ||
        (d.decision === 'approved' && d.origin === challenge.destination)),
  )
  return answered ? null : challenge
}

/** `'update'` once the policy has published, or when it was adopted from the
 *  Content Library (it was live before Studio); `'new'` otherwise. */
export const workType = (
  decisions: PolicyDecision[],
  meta?: { adopted?: boolean },
): 'new' | 'update' =>
  meta?.adopted || decisions.some((d) => d.decision === 'published')
    ? 'update'
    : 'new'

/**
 * For a policy currently in validation after a challenge sent it elsewhere
 * and it came back: `'responded'` when the latest decision is a response,
 * `'changed'` when the latest decision is a design approval following an
 * earlier challenge (since the previous design approval). `null` otherwise.
 */
export const returning = (
  decisions: PolicyDecision[],
): 'changed' | 'responded' | null => {
  if (!decisions.length) return null
  const latest = decisions.reduce((a, b) => (b.seq > a.seq ? b : a))
  if (latest.decision === 'responded') return 'responded'
  if (latest.decision === 'approved' && latest.origin === 'design') {
    const priorDesignApprovals = decisions.filter(
      (d) =>
        d.decision === 'approved' &&
        d.origin === 'design' &&
        d.seq < latest.seq,
    )
    const previousApproval = priorDesignApprovals.length
      ? priorDesignApprovals.reduce((a, b) => (b.seq > a.seq ? b : a))
      : null
    const since = previousApproval ? previousApproval.seq : -1
    const hadChallenge = decisions.some(
      (d) => d.decision === 'challenged' && d.seq > since && d.seq < latest.seq,
    )
    if (hadChallenge) return 'changed'
  }
  return null
}
